import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, DRC_CENTER, DRC_MAP_ZOOM, CITY_MAP_ZOOM, formatApiError, formatCDF } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AddressSearch from "../../components/AddressSearch";
import MapView from "../../components/MapView";
import AiInsightCard from "../../components/AiInsightCard";
import { getPreciseLocation } from "../../lib/preciseLocation";
import { reverseGeocode } from "../../lib/geo";
import { Button, Badge, Card, Input, Label } from "../../components/ui-kit";
import { ArrowLeft, Car, MapPin, Motorcycle, Package, ShieldCheck } from "@phosphor-icons/react";

const PACKAGE_SIZES = [
  { key: "small", label: "Petit", note: "Documents, petit sac" },
  { key: "medium", label: "Moyen", note: "Colis standard" },
  { key: "large", label: "Grand", note: "Carton ou gros sac" },
  { key: "fragile", label: "Fragile", note: "A manipuler avec soin" },
];

const VEHICLES = [
  { key: "moto", label: "Moto", icon: Motorcycle, note: "Rapide pour petits colis" },
  { key: "economy", label: "Voiture", icon: Car, note: "Colis moyen ou volumineux" },
];

export default function DeliveryBooking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [vehicleType, setVehicleType] = useState("moto");
  const [packageSize, setPackageSize] = useState("small");
  const [packageDescription, setPackageDescription] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [estimate, setEstimate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    api.get("/cities").then(({ data }) => setCities(Array.isArray(data) ? data : [])).catch(() => setCities([]));
  }, []);

  const currentCity = useMemo(() => cities.find((x) => x.id === user?.city_id), [cities, user?.city_id]);
  const cityCenter = useMemo(() => currentCity ? { lat: currentCity.lat, lng: currentCity.lng } : null, [currentCity]);
  const mapCenter = currentCity ? (destination || pickup || cityCenter || DRC_CENTER) : DRC_CENTER;
  const mapZoom = currentCity ? CITY_MAP_ZOOM : DRC_MAP_ZOOM;

  const preciseCurrentAddress = useCallback(async () => {
    const loc = await getPreciseLocation();
    const place = await reverseGeocode(loc.lat, loc.lng);
    return {
      ...loc,
      address: place?.label || `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}`,
      label: place?.label,
      short: place?.short,
      map_data: place || null,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!currentCity || pickup) return undefined;
    preciseCurrentAddress()
      .then((location) => {
        if (!cancelled) setPickup(location);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentCity, pickup, preciseCurrentAddress]);

  const estimateDelivery = useCallback(async () => {
    if (!pickup || !destination || !currentCity) return;
    try {
      const { data } = await api.post("/deliveries/estimate", {
        pickup,
        destination,
        vehicle_type: vehicleType,
        city_id: currentCity.id,
        package_size: packageSize,
      });
      setEstimate(data);
    } catch (e) {
      setEstimate(null);
      toast.error(formatApiError(e));
    }
  }, [pickup, destination, currentCity, vehicleType, packageSize]);

  useEffect(() => {
    estimateDelivery();
  }, [estimateDelivery]);

  const useMyLocation = async () => {
    if (!currentCity) {
      toast.error("Choisissez votre ville dans le profil avant de commander");
      return;
    }
    setLocating(true);
    try {
      const loc = await preciseCurrentAddress();
      setPickup(loc);
      toast.success("Adresse de depart detectee");
    } catch {
      toast.error("Impossible de detecter la position actuelle");
    } finally {
      setLocating(false);
    }
  };

  const createDelivery = async () => {
    if (!pickup || !destination || !currentCity) {
      toast.error("Ajoutez le depart, la destination et votre ville");
      return;
    }
    if (!packageDescription.trim() || !recipientName.trim() || !recipientPhone.trim()) {
      toast.error("Description colis, nom et telephone destinataire requis");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/deliveries", {
        pickup,
        destination,
        vehicle_type: vehicleType,
        city_id: currentCity.id,
        package_size: packageSize,
        package_description: packageDescription.trim(),
        recipient_name: recipientName.trim(),
        recipient_phone: recipientPhone.trim(),
        delivery_note: deliveryNote.trim() || null,
        payment_method: paymentMethod,
      });
      toast.success("Livraison creee - recherche livreur");
      navigate(`/app/ride/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  if (!currentCity) {
    return (
      <div className="phone-frame bg-premium-grad p-4">
        <button onClick={() => navigate("/app/services")} className="rounded-full glass-strong p-3">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <Card className="mt-6 p-5">
          <Badge tone="warning">Ville requise</Badge>
          <h1 className="mt-3 font-heading text-2xl font-black">Choisissez votre ville</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            La livraison est limitee a votre ville de service. Allez dans le profil et selectionnez une ville existante.
          </p>
          <Button className="mt-4 w-full" onClick={() => navigate("/app/profile")}>Aller au profil</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="phone-frame bg-premium-grad pb-28">
      <div className="relative h-[40svh] min-h-[300px] overflow-hidden">
        <MapView
          center={mapCenter}
          pickup={pickup}
          destination={destination}
          driverVehicleType={vehicleType}
          height="100%"
          zoom={mapZoom}
          showAttribution={false}
        />
        <div className="map-top-controls-safe absolute inset-x-0 z-[800] flex items-center justify-between gap-2">
          <button onClick={() => navigate("/app/services")} className="map-top-button rounded-full glass-strong p-3 press-down">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="map-top-pill rounded-full glass-strong px-4 py-2 text-[10px] font-bold uppercase tracking-widest">
            Livraison colis
          </div>
          <button onClick={useMyLocation} disabled={locating} className="map-top-button rounded-full glass-strong p-3 press-down disabled:opacity-60">
            <MapPin size={18} weight="fill" />
          </button>
        </div>
      </div>

      <div className="-mt-8 space-y-4 px-4">
        <Card className="relative z-[810] p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary">
              <Package size={24} weight="duotone" />
            </span>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-primary">NEXORA Delivery</div>
              <h1 className="font-heading text-2xl font-black">Envoyer un colis</h1>
              <p className="mt-1 text-xs text-muted-foreground">
                Un chauffeur ou taximan recupere le colis et le livre au destinataire avec suivi live.
              </p>
            </div>
          </div>
        </Card>

        <AiInsightCard
          endpoint="/ai/assistant"
          title="IA livraison"
          limit={2}
          compact
        />

        <Card className="p-4">
          <div className="grid gap-3">
            <div>
              <Label>Adresse de recuperation</Label>
              <AddressSearch
                cityCenter={cityCenter}
                placeholder="Ou recuperer le colis ?"
                valueLabel={pickup?.short || pickup?.address || ""}
                onSelect={(r) => setPickup({ lat: r.lat, lng: r.lng, address: r.label, map_data: r })}
              />
            </div>
            <div>
              <Label>Adresse de livraison</Label>
              <AddressSearch
                cityCenter={cityCenter}
                placeholder="Ou livrer le colis ?"
                valueLabel={destination?.short || destination?.address || ""}
                onSelect={(r) => setDestination({ lat: r.lat, lng: r.lng, address: r.label, map_data: r })}
              />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <Label>Type de colis</Label>
          <div className="grid grid-cols-2 gap-2">
            {PACKAGE_SIZES.map((item) => (
              <button
                key={item.key}
                onClick={() => setPackageSize(item.key)}
                className={`rounded-2xl border p-3 text-left press-down ${packageSize === item.key ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5"}`}
              >
                <div className="text-sm font-black">{item.label}</div>
                <div className="mt-1 text-[10px] text-muted-foreground">{item.note}</div>
              </button>
            ))}
          </div>
          <div className="mt-3">
            <Label>Description du colis</Label>
            <textarea
              value={packageDescription}
              onChange={(e) => setPackageDescription(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ex: documents, petit sac noir, repas emballe..."
              className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </Card>

        <Card className="p-4">
          <Label>Vehicule livreur</Label>
          <div className="grid grid-cols-2 gap-2">
            {VEHICLES.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => setVehicleType(item.key)}
                  className={`rounded-2xl border p-3 text-left press-down ${vehicleType === item.key ? "border-secondary/60 bg-secondary/15" : "border-white/10 bg-white/5"}`}
                >
                  <Icon size={24} weight="duotone" className="mb-2 text-secondary" />
                  <div className="text-sm font-black">{item.label}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground">{item.note}</div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="p-4">
          <div className="grid gap-3">
            <div>
              <Label>Nom destinataire</Label>
              <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Nom complet" />
            </div>
            <div>
              <Label>Telephone destinataire</Label>
              <Input value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} placeholder="+243..." inputMode="tel" />
            </div>
            <div>
              <Label>Instruction de livraison</Label>
              <Input value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder="Bureau, entree, personne a appeler..." />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <Label>Paiement</Label>
          <div className="grid grid-cols-2 gap-2 min-[390px]:grid-cols-4">
            {[
              { k: "cash", l: "Cash" },
              { k: "mpesa", l: "M-Pesa" },
              { k: "airtel", l: "Airtel" },
              { k: "orange", l: "Orange" },
            ].map((item) => (
              <button
                key={item.k}
                onClick={() => setPaymentMethod(item.k)}
                className={`rounded-xl px-2 py-2 text-[10px] font-bold uppercase tracking-widest ${paymentMethod === item.k ? "bg-secondary text-secondary-foreground" : "bg-white/5 text-muted-foreground"}`}
              >
                {item.l}
              </button>
            ))}
          </div>
          <div className="mt-3 rounded-2xl bg-white/5 p-3 text-xs text-muted-foreground">
            Le client paie directement au chauffeur/livreur. Le livreur confirme la reception.
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Estimation</div>
              <div className="font-heading text-3xl font-black text-primary">{estimate ? formatCDF(estimate.price) : "--"}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {estimate ? `${estimate.distance_km} km - ${Math.round(estimate.duration_min)} min` : "Ajoutez les adresses pour calculer"}
              </div>
            </div>
            <ShieldCheck size={36} weight="duotone" className="text-secondary" />
          </div>
          <Button className="mt-4 w-full py-4 text-base" onClick={createDelivery} disabled={!estimate || creating}>
            {creating ? "Creation..." : `Demander livraison ${estimate ? "- " + formatCDF(estimate.price) : ""}`}
          </Button>
        </Card>
      </div>
    </div>
  );
}
